import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LAYERS, portalHost } from '../lib/floating';

/**
 * Hover affordances, in one place.
 *
 * Three of them, and the rule they share is that **hover only ever reveals
 * something already true**. Nothing here is the only way to reach information,
 * because hover does not exist on a phone and does not exist for anybody
 * driving the console from the keyboard. Every one of these also opens on
 * focus, and none of them hides anything you would be stuck without.
 *
 * That constraint is why there is no hover-to-delete and no hover-only action
 * in this file. A control you have to find by waving a mouse at the screen is a
 * control half the team will never find.
 */

/* ── Anchoring ───────────────────────────────────────────────────────────── */

type Side = 'top' | 'bottom' | 'left' | 'right';

/**
 * Where a panel should sit relative to the thing it belongs to, in viewport
 * coordinates, so it can be rendered through a portal.
 *
 * The portal is not decoration. Both panels below get used inside tables that
 * live in `overflow-x-auto` wrappers — the tickets table is one — and a CSS
 * `absolute` panel inside a scroll container gets clipped by it, or worse,
 * grows the container a scrollbar. Rendering to `document.body` with
 * `position: fixed` is the only way a panel escapes an ancestor that clips.
 *
 * Measured on open rather than tracked continuously: these panels close the
 * moment the pointer leaves, so a scroll mid-hover is not a case worth the
 * listener. `flip` keeps a panel near the bottom of a phone-height window on
 * screen, which is the one measurement that actually earns its keep.
 */
function anchor(rect: DOMRect, side: Side, size: { w: number; h: number }) {
  const gap = 8;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Flip when the preferred side has no room. Only the axis in play flips —
  // a tooltip that wanted to be above and cannot does not become a left one.
  let resolved = side;
  if (side === 'top' && rect.top - size.h - gap < margin) resolved = 'bottom';
  if (side === 'bottom' && rect.bottom + size.h + gap > vh - margin) resolved = 'top';
  if (side === 'left' && rect.left - size.w - gap < margin) resolved = 'right';
  if (side === 'right' && rect.right + size.w + gap > vw - margin) resolved = 'left';

  let top: number;
  let left: number;

  switch (resolved) {
    case 'top':
      top = rect.top - size.h - gap;
      left = rect.left + rect.width / 2 - size.w / 2;
      break;
    case 'bottom':
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2 - size.w / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - size.h / 2;
      left = rect.left - size.w - gap;
      break;
    default:
      top = rect.top + rect.height / 2 - size.h / 2;
      left = rect.right + gap;
  }

  // Then keep it inside the window regardless of side. A reference in the
  // last column of a wide table would otherwise put half its card off screen.
  return {
    top: Math.min(Math.max(top, margin), Math.max(margin, vh - size.h - margin)),
    left: Math.min(Math.max(left, margin), Math.max(margin, vw - size.w - margin)),
  };
}

/**
 * Open/closed state plus the measured position, shared by both panels.
 *
 * `openAfter` is a delay in milliseconds. Zero for a tooltip, which should feel
 * immediate on a control you have deliberately pointed at; 350 for a card,
 * because one that appears the instant the pointer crosses a row flickers its
 * way down a list of twenty and makes the page feel broken.
 */
function useHoverPanel(openAfter: number) {
  const trigger = useRef<HTMLSpanElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  // Position is not known until the panel has been measured, so it opens at a
  // provisional spot and `place` corrects it once the size is real. Without
  // this a wrapping tooltip sits half a line too low.
  const place = useCallback((size: { w: number; h: number }, side: Side) => {
    const rect = trigger.current?.getBoundingClientRect();
    if (rect) setAt(anchor(rect, side, size));
  }, []);

  const open = (delay = openAfter) => {
    cancel();
    const show = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (rect) setAt(anchor(rect, 'top', { w: 0, h: 0 }));
    };
    if (delay === 0) show();
    else timer.current = setTimeout(show, delay);
  };

  const close = () => {
    cancel();
    setAt(null);
  };

  // A pending timer outliving its row is a `setState` on an unmounted node.
  // Lists here re-render on every poll, so this is a real case, not a tidy-up.
  useEffect(() => cancel, []);

  return { trigger, at, isOpen: at !== null, open, close, place };
}

/* ── Tooltip ─────────────────────────────────────────────────────────────── */

/**
 * A short explanation attached to a control.
 *
 * Replaces `title=""`, which the browser renders after about a second in a
 * style nobody can read outdoors, and which never appears for a keyboard user
 * at all. This one opens on hover *and* on focus, and is wired with
 * `aria-describedby` so a screen reader gets it too.
 *
 * Deliberately not a library. It is a positioned div and two state changes.
 */
export function Tooltip({
  label,
  children,
  side = 'top',
}: {
  label: string;
  children: React.ReactNode;
  side?: Side;
}) {
  const panel = useRef<HTMLSpanElement | null>(null);
  const { trigger, at, isOpen, open, close, place } = useHoverPanel(0);
  const id = useId();

  useEffect(() => {
    if (!isOpen || !panel.current) return;
    const { width, height } = panel.current.getBoundingClientRect();
    place({ w: width, h: height }, side);
    // Runs once per opening. `at` is deliberately absent from the deps: it is
    // what this writes, and including it would loop.
  }, [isOpen, side, place, label]);

  return (
    <>
      <span
        ref={trigger}
        className="relative inline-flex"
        onMouseEnter={() => open()}
        onMouseLeave={close}
        onFocus={() => open()}
        onBlur={close}
      >
        <span aria-describedby={isOpen ? id : undefined}>{children}</span>
      </span>

      {at !== null &&
        createPortal(
          <span
            ref={panel}
            id={id}
            role="tooltip"
            style={{ top: at.top, left: at.left, zIndex: LAYERS.explain }}
            className="pointer-events-none fixed w-max max-w-56 whitespace-normal
              rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-xs
              font-medium text-white"
          >
            {label}
          </span>,
          portalHost(),
        )}
    </>
  );
}

/* ── Hover card ──────────────────────────────────────────────────────────── */

/**
 * A summary shown when the pointer rests on a link.
 *
 * For the case where a row tells you a name and you want the two or three facts
 * behind it without losing your place in the list — a customer's package and
 * phone number, a contract's term and standing.
 *
 * **The link still works, and still goes to the full record.** The card is a
 * preview, never a substitute: the same facts are on the page it opens, so
 * somebody who never hovers loses nothing.
 */
export function HoverCard({
  children,
  title,
  facts,
  note,
  side = 'bottom',
}: {
  children: React.ReactNode;
  title: string;
  /** Two to five short pairs. Longer than that and it wants to be a page. */
  facts: Array<{ label: string; value: string }>;
  note?: string;
  side?: Side;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const { trigger, at, isOpen, open, close, place } = useHoverPanel(350);

  useEffect(() => {
    if (!isOpen || !panel.current) return;
    const { width, height } = panel.current.getBoundingClientRect();
    place({ w: width, h: height }, side);
    // As above: `at` is what this effect writes, so depending on it would loop.
  }, [isOpen, side, place, title]);

  return (
    <>
      <span
        ref={trigger}
        className="relative inline-flex"
        onMouseEnter={() => open()}
        onMouseLeave={close}
        // No delay from the keyboard: you did not arrive on this link by
        // accident, so there is nothing to wait out.
        onFocus={() => open(0)}
        onBlur={close}
      >
        {children}
      </span>

      {at !== null &&
        createPortal(
          <div
            ref={panel}
            role="tooltip"
            style={{ top: at.top, left: at.left, zIndex: LAYERS.explain }}
            className="pointer-events-none fixed w-64 whitespace-normal rounded-xl
              border border-border bg-card p-3 text-left"
          >
            <p className="font-heading text-sm font-bold text-navy-700">{title}</p>

            <dl className="mt-2 space-y-1">
              {facts.map((fact) => (
                <div key={fact.label} className="flex justify-between gap-3 text-xs">
                  <dt className="text-muted-foreground">{fact.label}</dt>
                  <dd className="text-right font-medium text-foreground">{fact.value}</dd>
                </div>
              ))}
            </dl>

            {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
          </div>,
          portalHost(),
        )}
    </>
  );
}

/* ── Image overlay ───────────────────────────────────────────────────────── */

/**
 * A photograph with its details over it on hover.
 *
 * Site photos are the one place in this system where the image *is* the record,
 * and the things worth knowing about one — when it was taken, by whom, at which
 * job — have nowhere to sit without covering the picture they describe.
 *
 * So they sit on top, on hover, over a navy scrim heavy enough that white text
 * on a photograph of a sunlit wall is still readable. The scrim is the point:
 * white-on-photo without one is unreadable roughly half the time and there is
 * no way to know in advance which half.
 *
 * On touch, where there is no hover, the caption below the image carries the
 * same facts — so nothing here is hover-only.
 */
export function ImageHoverInfo({
  src,
  alt,
  title,
  facts,
  onOpen,
}: {
  src: string;
  alt: string;
  title: string;
  facts: string[];
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative block w-full overflow-hidden rounded-xl border border-border
        bg-muted text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-700"
    >
      <img
        src={src}
        alt={alt}
        // Lazy because a long job can carry a dozen of these and they come down
        // the line from Kisii, not from a CDN.
        loading="lazy"
        className="aspect-[4/3] w-full object-cover transition-transform duration-300
          motion-safe:group-hover:scale-[1.03]"
      />

      {/*
        The scrim and the text share one opacity transition, so they arrive
        together rather than the words appearing before anything darkens behind
        them.
      */}
      <span
        className="absolute inset-0 flex flex-col justify-end bg-navy-900/80 p-3 opacity-0
          transition-opacity duration-200 group-hover:opacity-100
          group-focus-visible:opacity-100"
      >
        <span className="font-heading text-sm font-bold text-white">{title}</span>
        {facts.map((fact) => (
          <span key={fact} className="mt-0.5 text-xs text-navy-100">
            {fact}
          </span>
        ))}
      </span>
    </button>
  );
}
